import { useState, useCallback, useEffect } from 'react';

export interface NavigationState {
  isMobileMenuOpen: boolean;
  isDesktopMenuOpen: boolean;
  activeMenuItem: string | null;
  isAnimating: boolean;
  previousFocusElement: HTMLElement | null;
}

export interface NavigationActions {
  openMobileMenu: () => void;
  closeMobileMenu: () => void;
  setActiveMenuItem: (item: string | null) => void;
  closeAllMenus: () => void;
  handleArrowNavigation: (direction: 'up' | 'down' | 'left' | 'right') => void;
}

export interface UseNavigationReturn extends NavigationState, NavigationActions {}

/**
 * Custom hook for managing navigation state, particularly mobile navigation
 * Handles menu open/close states, animations, and accessibility features
 */
export const useNavigation = (): UseNavigationReturn => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDesktopMenuOpen, setIsDesktopMenuOpen] = useState(false);
  const [activeMenuItem, setActiveMenuItem] = useState<string | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [previousFocusElement, setPreviousFocusElement] = useState<HTMLElement | null>(null);

  // Define callbacks before useEffect hooks that use them
  const openMobileMenu = useCallback(() => {
    // Store current focus element before opening menu
    setPreviousFocusElement(document.activeElement as HTMLElement);

    setIsAnimating(true);
    setIsMobileMenuOpen(true);

    // Announce menu opened for screen readers
    const announcement = document.createElement('div');
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', 'polite');
    announcement.classList.add('sr-only');
    announcement.textContent = 'Navigation menu opened';
    document.body.appendChild(announcement);
    setTimeout(() => document.body.removeChild(announcement), 1000);

    // Reset animation state after transition
    setTimeout(() => setIsAnimating(false), 300);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setIsAnimating(true);
    setIsMobileMenuOpen(false);
    setActiveMenuItem(null);

    // Announce menu closed for screen readers
    const announcement = document.createElement('div');
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', 'polite');
    announcement.classList.add('sr-only');
    announcement.textContent = 'Navigation menu closed';
    document.body.appendChild(announcement);
    setTimeout(() => document.body.removeChild(announcement), 1000);

    // Return focus to previous element
    if (previousFocusElement) {
      setTimeout(() => {
        previousFocusElement?.focus();
        setPreviousFocusElement(null);
      }, 300);
    }

    // Reset animation state after transition
    setTimeout(() => setIsAnimating(false), 300);
  }, [previousFocusElement]);

  const closeAllMenus = useCallback(() => {
    setIsMobileMenuOpen(false);
    setIsDesktopMenuOpen(false);
    setActiveMenuItem(null);
    setIsAnimating(false);
    setPreviousFocusElement(null);
  }, []);

  // Handle arrow key navigation for desktop menu
  const handleArrowNavigation = useCallback((direction: 'up' | 'down' | 'left' | 'right') => {
    const focusableItems = document.querySelectorAll(
      '.desktop-nav a[href], .desktop-nav button:not([disabled])'
    );
    const currentIndex = Array.from(focusableItems).findIndex(
      (item) => item === document.activeElement
    );

    if (currentIndex === -1) return;

    let nextIndex = currentIndex;

    switch (direction) {
      case 'left':
      case 'up':
        nextIndex = currentIndex > 0 ? currentIndex - 1 : focusableItems.length - 1;
        break;
      case 'right':
      case 'down':
        nextIndex = currentIndex < focusableItems.length - 1 ? currentIndex + 1 : 0;
        break;
    }

    (focusableItems[nextIndex] as HTMLElement)?.focus();
  }, []);

  // Handle escape key press and arrow navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isMobileMenuOpen) {
        closeMobileMenu();
        // Return focus to trigger element
        if (previousFocusElement) {
          previousFocusElement.focus();
        }
      }

      // Arrow key navigation for desktop menu
      if (
        isDesktopMenuOpen &&
        ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)
      ) {
        event.preventDefault();
        const direction = event.key.replace('Arrow', '').toLowerCase() as
          | 'up'
          | 'down'
          | 'left'
          | 'right';
        handleArrowNavigation(direction);
      }
    };

    if (isMobileMenuOpen || isDesktopMenuOpen) {
      document.addEventListener('keydown', handleKeyDown);

      // Prevent body scrolling when mobile menu is open
      if (isMobileMenuOpen) {
        document.body.style.overflow = 'hidden';
        // Add aria-hidden to main content
        const mainContent = document.querySelector('main');
        if (mainContent) {
          mainContent.setAttribute('aria-hidden', 'true');
        }
      }
    } else {
      document.body.style.overflow = 'unset';
      const mainContent = document.querySelector('main');
      if (mainContent) {
        mainContent.removeAttribute('aria-hidden');
      }
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
      const mainContent = document.querySelector('main');
      if (mainContent) {
        mainContent.removeAttribute('aria-hidden');
      }
    };
  }, [
    isMobileMenuOpen,
    isDesktopMenuOpen,
    closeMobileMenu,
    handleArrowNavigation,
    previousFocusElement,
  ]);

  // Handle window resize to close mobile menu on larger screens
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768 && isMobileMenuOpen) {
        closeMobileMenu();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobileMenuOpen, closeMobileMenu]);

  return {
    // State
    isMobileMenuOpen,
    isDesktopMenuOpen,
    activeMenuItem,
    isAnimating,
    previousFocusElement,

    // Actions
    openMobileMenu,
    closeMobileMenu,
    setActiveMenuItem,
    closeAllMenus,
    handleArrowNavigation,
  };
};
